import { DatePeriodSelectProps } from '@unistocks/components/date-period-select/date-period-select';
import { DateConstant } from '@unistocks/proxy/axios-client';
import { LineChartPoint } from './types';

/**
 * Summary statistics for a line chart series
 */
export type LineChartSeriesSummary = {
  changePercent: number;
  changeNominal: number;
  value: number;
};

export type { LineChartPoint };

////////////////////////////////////////////////////////////////////////////////
// Shared tooltip/header layout metrics (web + native). The chart header is
// rendered as an overlay on top of the chart, and the tooltip is pinned to
// the top of the chart above the header in z-order. Both renderers reserve
// matching vertical space above the line area so neither overlaps the lines.
////////////////////////////////////////////////////////////////////////////////

/** Vertical gap between tooltip rows */
export const TOOLTIP_ROW_GAP = 8;
export { TOOLTIP_COL_GAP, TOOLTIP_MARKER_WIDTH } from '../tooltip';
/** Gap between the reserved top band (header/tooltip) and the line area */
export const TOOLTIP_HEADER_GAP = 8;
/** Line-height factor used to estimate a tooltip row from its font size */
export const TOOLTIP_ROW_LINE_HEIGHT_FACTOR = 1.25;
/**
 * Maximum fraction of the chart container height that the reserved top band
 * may consume, so a short tile can never squash the line area to zero.
 */
export const RESERVED_TOP_MAX_FRACTION = 0.6;

/**
 * Estimates the pixel height of the pinned tooltip box.
 * Rows top to bottom: date, primary (label + value + percent), then one row
 * per comparison series with data at the current index.
 */
export const getTooltipHeightPx = (rows: number, fontSize: number, padding: number) =>
  padding * 2 + rows * fontSize * TOOLTIP_ROW_LINE_HEIGHT_FACTOR + TOOLTIP_ROW_GAP * (rows - 1);

/**
 * Pixel band reserved above the line area. The header and the pinned tooltip
 * share the same top band (the tooltip renders above the header in z-order),
 * so the reservation covers the taller of the two plus a gap. It is capped
 * at a fraction of the container height.
 */
export const getReservedTopPx = (
  headerHeightPx: number,
  tooltipHeightPx: number,
  containerHeightPx: number
) => {
  const desired = Math.max(headerHeightPx, tooltipHeightPx) + TOOLTIP_HEADER_GAP;
  if (!(containerHeightPx > 0)) return Math.max(desired, 0);
  return Math.min(desired, containerHeightPx * RESERVED_TOP_MAX_FRACTION);
};

/**
 * Min/Max point structure for chart markers
 */
export type MinMaxPoint = {
  x: number;
  y: number;
  xValue: number;
  yValue: number;
};

export type MinMaxPoints = {
  minPoint: MinMaxPoint | null;
  maxPoint: MinMaxPoint | null;
};

/**
 * Finds the minimum and maximum value points from chart data
 */
export function createMinMaxPoints<
  T extends { x?: number; y?: number; xValue: number; yValue: number | null }
>(points: T[]): MinMaxPoints {
  return points.reduce(
    (acc, point) => {
      if (point.yValue != null) {
        if (acc.minPoint?.yValue == null || point.yValue < acc.minPoint.yValue) {
          acc.minPoint = {
            x: point.x ?? 0,
            y: point.y ?? 0,
            xValue: point.xValue,
            yValue: point.yValue
          };
        }
        if (acc.maxPoint?.yValue == null || point.yValue > acc.maxPoint.yValue) {
          acc.maxPoint = {
            x: point.x ?? 0,
            y: point.y ?? 0,
            xValue: point.xValue,
            yValue: point.yValue
          };
        }
      }

      return acc;
    },
    { minPoint: null, maxPoint: null } as MinMaxPoints
  );
}

/**
 * Filter function for date period select buttons - excludes certain date constants
 */
export const dateButtonsFilter: DatePeriodSelectProps['filterOptions'] = ({ value }) =>
  ![DateConstant.OneDay, DateConstant.SixMonths, DateConstant.FiveYears, DateConstant.Max].includes(
    value
  );
